import * as React from "react";
import {TextPresenter} from "./presenter/TextPresenter";
import DraggableTable from "./DraggableTable";
import TimePeriodPresenter from "./presenter/TimePeriodPresenter";
import {JsonPresenter} from "./presenter/JsonPresenter";
import TableColumnSelector from "./TableColumnSelector";
import {CellInfo, Column, Resize, RowInfo} from "react-table";
import TableHeaderCell from "./headerCell/TableHeaderCell";
import {none, Option, option} from "ts-option";
import {AppointmentPresenter} from "./presenter/AppointmentPresenter";
import Checkbox from "../../../hiberUiKit/src/atoms/Checkbox/Checkbox";
import {TagsPresenter} from "./presenter/TagsPresenter";
import {ViewColumn} from "../../../model/workspace/ViewColumn";
import {ColumnDefinition, ColumnTypeEnum} from "../../../model/workspace/ColumnDefinition";
import {ContactPresenter} from "./presenter/ContactPresenter";
import SortColumnModel from "../../../model/workspace/SortColumnModel";
import {CreatorPresenter} from "./presenter/creator/CreatorPresenter";
import {ReconciliationStatusPresenter} from "./presenter/reconciliation/ReconciliationStatusPresenter";
import {AssigneesPresenter} from "./presenter/AssigneesPresenter";
import {CurrencyPresenter} from "./presenter/CurrencyPresenter";
import {PaymentMethodPresenter} from "./presenter/PaymentMethodPresenter";
import {CustomFieldPresenter} from "./presenter/CustomFieldPresenter";
import {BankAccountTypePresenter} from "./presenter/BankAccountTypePresenter";
import {UserPresenter} from "./presenter/user/UserPresenter";
import TextUtils from "../../../utils/TextUtils";
import Toast from "../../helper/Toast";
import {PercentagePresenter} from "./presenter/PercentagePresenter";
import {BooleanStatusPresenter} from "./presenter/BooleanStatusPresenter";
import {isDefined} from "../../../utils/Utils";
import {UserPhonePresenter} from "./presenter/user/UserPhonePresenter";
import {BooleanPresenter} from "./presenter/BooleanPresenter";
import {JobInvoicePathPresenter} from "./presenter/JobInvoicePathPresenter";
import {arrayMove} from "../../../utils/ArrayUtils";
import WorkspaceCardList from "./WorkspaceCardList";
import moment = require("moment");
import deepEqual = require("deep-equal");
import _ = require("underscore");
import classNames = require("classnames");

interface IState {
	columnSelectorDropdownVisible: boolean,
	skipNextSort: boolean
	selectedCheckboxes: string[];
	hoveredCell: Option<IHoveredCell>;
	isAltPressed: boolean;
	enableRowClick: boolean;
	/**
	 * Indicates if the screen is a phone's, where rows show as cards rather than as a table.
	 */
	isPhone: boolean;
}

interface IProps {
	columns: ViewColumn[];
	hideColumnCheckbox?: boolean;
	onColumnsChange: (columns: ViewColumn[]) => void;
	data: any[];
	onRowClick?: (rowId: string, rowData: any) => void;
	rowToId: (row: any) => string;
	isLoading?: boolean;
	/**
	 * Returns if passed row is ignored.
	 * @param rowData
	 * @returns {boolean}
	 */
	isRowIgnored?: (rowData: any) => boolean;
	/**
	 * Text displayed when no rows were found.
	 */
	noDataText?: string;
	onSortingChange: (sortedColumn: Option<SortColumnModel>) => void;
	sortColumn: Option<SortColumnModel>,
	sortable?: boolean;
	lastColumn?: (row: CellInfo) => JSX.Element;
	/**
	 * Custom width of last column.
	 */
	lastColumnWidth?: number;
	/**
	 * Reload current page data.
	 */
	refreshCurrentPage: () => void;
	/**
	 * Workspace name to display in toast.
	 */
	workspaceName: string,
	/**
	 * On checkbox selection change
	 */
	onSelectedCheckboxesChange?: (ids: string[]) => void
	/**
	 * Size of the page displayed
	 */
	pageSize?: number
}

interface IHoveredCell {
	viewColumn: ViewColumn
	value: any;
}

export default class DataList extends React.Component<IProps, IState> {
	public static defaultProps: Partial<IProps> = {
		lastColumnWidth: 100
	};

	/**
	 * The width below which rows show as cards; workspaceCardList.scss and the workspace's own styles use it too.
	 */
	phoneQuery: MediaQueryList | null = window.matchMedia ? window.matchMedia("(max-width: 640px)") : null;

	constructor(props: IProps) {
		super(props);
		this.state = {
			columnSelectorDropdownVisible: false,
			skipNextSort: false,
			selectedCheckboxes: [],
			hoveredCell: none,
			isAltPressed: false,
			enableRowClick: true,
			isPhone: !!this.phoneQuery && this.phoneQuery.matches
		};

		this.onReOrder = this.onReOrder.bind(this);
		this.getTheadThProps = this.getTheadThProps.bind(this);
		this.getTrProps = this.getTrProps.bind(this);
		this.changeSortOrder = this.changeSortOrder.bind(this);
		this.onResize = this.onResize.bind(this);
		this.handleKeyDown = this.handleKeyDown.bind(this);
		this.handleKeyUp = this.handleKeyUp.bind(this);
	}

	componentWillMount() {
		document.addEventListener("keydown", this.handleKeyDown);
		document.addEventListener("keyup", this.handleKeyUp);
		if (this.phoneQuery) {
			this.phoneQuery.addListener(this.onPhoneChange);
		}
	}

	componentWillUnmount() {
		document.removeEventListener("keydown", this.handleKeyDown);
		document.removeEventListener("keyup", this.handleKeyUp);
		if (this.phoneQuery) {
			this.phoneQuery.removeListener(this.onPhoneChange);
		}
	}

	onPhoneChange = (query: MediaQueryList) => this.setState({isPhone: query.matches});

	handleKeyDown(e: any) {
		if (e.keyCode == 18) {
			this.setState({isAltPressed: true});
		}
	}

	handleKeyUp(e: any) {
		if (e.keyCode == 18) {
			this.setState({isAltPressed: false});
		}
	}

	render() {
		if (this.state.isPhone) {
			return <WorkspaceCardList columns={this.props.columns}
									  data={this.props.data}
									  rowToId={this.props.rowToId}
									  onRowClick={this.props.onRowClick}
									  isLoading={this.props.isLoading}
									  noDataText={this.props.noDataText}
									  renderValue={(column, value, row) =>
										  this.getCellPresenter(column.columnDefinition, value, row)}/>;
		}
		const displayedColumns: Column[] = this.props.hideColumnCheckbox ? [] : [{
			Header: this.SelectAllCheckbox,
			Cell: this.RowCheckbox,
			width: 50
		}];
		let allColumnsHaveWidth = true;
		displayedColumns.push(...this.props.columns.filter(c => c.visible).map(dc => {
			if (dc.width.isEmpty) {
				allColumnsHaveWidth = false;
			}
			return {
				id: dc.id,
				Header: <TableHeaderCell name={dc.columnDefinition.label}
										 isAsc={this.props.sortColumn.map(
											 sc => sc.columnId === dc.columnDefinition.id ? option(sc.isAsc) : none).getOrElse(
											 none)}
										 isDraggable={true}/>,
				accessor: dc.columnDefinition.path,
				Cell: (props: CellInfo) => this.Cell(props, dc),
				width: dc.width.getOrElse(undefined),
				sortable: this.props.sortable && dc.columnDefinition.sortable,
				sortPath: dc.columnDefinition.sortPath,
				columnDefinitionId: dc.columnDefinition.id
			}
		}));
		displayedColumns.push({
			Header: <TableColumnSelector columns={this.props.columns}
										 onChange={(columns) => this.setState({columnSelectorDropdownVisible: true},
											 () => this.props.onColumnsChange(columns))}
										 dropDownMenuVisible={this.state.columnSelectorDropdownVisible}
										 onDropdownMenuVisibilityChange={(columnSelectorDropdownVisible) =>
											 this.setState({columnSelectorDropdownVisible})}/>,
			resizable: false,
			Cell: (props: CellInfo) => this.props.lastColumn ? this.props.lastColumn(props) : null,
			width: (displayedColumns.length - 1 && !allColumnsHaveWidth) ? this.props.lastColumnWidth : undefined
		});
		return (
			<DraggableTable data={this.props.data}
							columns={displayedColumns}
							sortable={false}
							showPaginationBottom={false}
							minRows={0}
							onResizedChange={this.onResize}
							getTrProps={this.getTrProps}
							onReOrder={this.onReOrder}
							getTheadThProps={this.getTheadThProps}
							loading={this.props.isLoading}
							noDataText={this.props.noDataText}
							pageSize={this.props.pageSize}/>

		)
	}

	clearCheckBoxSelection = () => {
		if (this.state.selectedCheckboxes.length > 0) {
			this.setState({selectedCheckboxes: []}, () => {
				if (this.props.onSelectedCheckboxesChange) {
					this.props.onSelectedCheckboxesChange([]);
				}
			})
		}
	}

	onCellMouseOver(e: React.MouseEvent<HTMLDivElement>, viewColumn: ViewColumn, value: any) {
		this.setState({hoveredCell: option({viewColumn, value}), isAltPressed: e.altKey});
	}

	onResize(newResized: Resize[]) {
		let cols = this.props.columns;
		newResized.forEach(rc =>
			cols = cols.map(c => c.id == rc.id ? c.copy({width: option(rc.value)}) : c)
		);
		this.setState({skipNextSort: true}, () => this.props.onColumnsChange(cols));
	}

	onReOrder(target: number, dragged: number) {
		const cols = [...this.props.columns];
		const visibleColumnIds = cols.filter(c => c.visible).map(c => c.id);

		const orgDraggedIndex = cols.map(c => c.id).indexOf(visibleColumnIds[dragged]);
		const orgTargetIndex = cols.map(c => c.id).indexOf(visibleColumnIds[target]);

		this.props.onColumnsChange(arrayMove(cols, orgDraggedIndex, orgTargetIndex));
	}

	getTrProps(state: any, rowInfo: RowInfo) {
		return {
			onClick: () =>
				this.props.onRowClick && this.state.enableRowClick &&
				this.props.onRowClick(this.props.rowToId(rowInfo.original), rowInfo.original),
			className: this.state.isAltPressed && this.state.hoveredCell.map(hc => hc.value ?
				this.isSame(hc, rowInfo.original) ? "same-row" : "different-row" : {}).orNull
		}
	}

	isSame(hc: IHoveredCell, original: any) {
		const orgValue = original[hc.viewColumn.columnDefinition.path];
		const columnValue = hc.value;

		if (ColumnTypeEnum.Date == hc.viewColumn.columnDefinition.type) {
			return moment(orgValue).isSame(moment(columnValue), "date");
		}

		if ([ColumnTypeEnum.Brand, ColumnTypeEnum.Contact, ColumnTypeEnum.LeadStatus, ColumnTypeEnum.JobStatus,
			ColumnTypeEnum.PartnerBrand, ColumnTypeEnum.Merchant, ColumnTypeEnum.JobType, ColumnTypeEnum.InvoicePath]
			.indexOf(hc.viewColumn.columnDefinition.type as ColumnTypeEnum) > -1) {
			return orgValue == columnValue
		}
		// TODO add equal definitions for other columns types

		return deepEqual(orgValue, columnValue)
	}

	getTheadThProps(state: any, rowInfo?: any, column?: any) {
		return column.columnDefinitionId && column.sortable ? {
			onClick: (e: any, handleOriginal: any) => {
				this.changeSortOrder(column.columnDefinitionId, column.sortPath);
				if (handleOriginal) {
					handleOriginal();
				}
			}
		} : {}
	}

	changeSortOrder(columnId: string, sortPath: string) {
		if (this.state.skipNextSort) {
			this.setState({skipNextSort: false});
		}
		else {
			const sortColumn = this.props.sortColumn.map(
				sc => {
					return new SortColumnModel(columnId, sortPath, sc.columnId == columnId ? !sc.isAsc : true)
				})
				.orElse(() => option(new SortColumnModel(columnId, sortPath, true)));
			this.props.onSortingChange(sortColumn)
		}
	}

	copyItemIdOrRefNumber = (evt: React.MouseEvent<HTMLDivElement>, value: string, rowData: any) => {
		if (value === (rowData.referenceNumber || rowData.id)) {
			const elementType = this.props.workspaceName.capitalize().slice(0, -1);
			evt.stopPropagation();
			TextUtils.copyToClipboard(value);
			Toast.success(`${elementType} id copied successfully`, "Copy to clipboard")
		}
	};

	getCellPresenter(columnDefinition: ColumnDefinition, value: any, rowData: any) {
		if (columnDefinition.type !== ColumnTypeEnum.LeadAssignee &&
			columnDefinition.type !== ColumnTypeEnum.JobAssignee &&
			(!isDefined(value) || (value instanceof Array && !value.length))) {
			return <span>-</span>
		}
		switch (columnDefinition.type) {
			case ColumnTypeEnum.Date:
				return <TimePeriodPresenter value={value}/>;
			case ColumnTypeEnum.Appointments:
				return <AppointmentPresenter value={value}/>;
			case ColumnTypeEnum.LeadAssignee:
			case ColumnTypeEnum.JobAssignee:
				return <AssigneesPresenter value={value}/>;
			case ColumnTypeEnum.LeadTag:
			case ColumnTypeEnum.JobTag:
				return <TagsPresenter value={value}/>;
			case ColumnTypeEnum.Contact:
				return <ContactPresenter value={value}/>;
			case ColumnTypeEnum.Creator:
				return <CreatorPresenter value={value}/>;
			case ColumnTypeEnum.User:
				return <UserPresenter value={value}/>;
			case ColumnTypeEnum.UserPhone:
				return <UserPhonePresenter value={value}/>;
			case ColumnTypeEnum.Reconciliation:
				return <ReconciliationStatusPresenter value={value}
													  onModalOpen={this.onModalOpen}
													  onModalClose={this.onModalClose}
													  transactionData={rowData}/>;
			case ColumnTypeEnum.Currency:
			case ColumnTypeEnum.Decimal:
				return <CurrencyPresenter value={value}/>;
			case ColumnTypeEnum.Percentage:
				return <PercentagePresenter value={value}/>;
			case ColumnTypeEnum.PaymentMethod:
				return <PaymentMethodPresenter value={value}/>;
			case ColumnTypeEnum.BankAccountType:
				return <BankAccountTypePresenter value={value}/>;
			case ColumnTypeEnum.LeadCustomField:
			case ColumnTypeEnum.JobCustomField:
				return <CustomFieldPresenter
					value={_.find((value), (item: any) => item.customFieldId === columnDefinition.customFieldId.orNull)}/>;
			case ColumnTypeEnum.StatusBoolean:
				return <BooleanStatusPresenter value={value}/>;
			case ColumnTypeEnum.Boolean:
				return <BooleanPresenter value={value}/>;
			case ColumnTypeEnum.InvoicePath:
				return <JobInvoicePathPresenter value={value}/>;
			default:
				return _.isString(value) ?
					<TextPresenter
						onClickHandler={(e) => this.copyItemIdOrRefNumber(e, value, rowData)}
						value={value}/>
					: <JsonPresenter value={value}/>;
		}
	}

	onModalOpen = () => this.setState({enableRowClick: false});
	onModalClose = () => this.setState({enableRowClick: true}, this.props.refreshCurrentPage);

	SelectAllCheckbox: React.SFC = () => {
		return <Checkbox checked={this.state.selectedCheckboxes.indexOf("all") > -1}
						 onChange={(value) => {
							 const selectedCheckboxes = [];
							 if (value) {
								 selectedCheckboxes.push("all");
								 if (this.props.pageSize) {
									 for (let i = 0; i < this.props.pageSize; i++) {
										 selectedCheckboxes.push(i.toString())
									 }
								 }
							 }
							 this.setState({selectedCheckboxes});
							 if (this.props.onSelectedCheckboxesChange) {
								 this.props.onSelectedCheckboxesChange(selectedCheckboxes);
							 }
						 }}/>
	};

	RowCheckbox: React.SFC<CellInfo> = (props) => {
		return (
			<Checkbox checked={this.state.selectedCheckboxes.indexOf("all") > -1 ||
			this.state.selectedCheckboxes.indexOf(`${props.index}`) > -1}
					  onChange={(value) => {
						  this.setState({
							  selectedCheckboxes: value ? this.state.selectedCheckboxes.concat(`${props.index}`) :
								  this.state.selectedCheckboxes.filter(s => s !== `${props.index}` && s !== 'all')
						  }, () => {
							  if (this.props.onSelectedCheckboxesChange) {
								  this.props.onSelectedCheckboxesChange(this.state.selectedCheckboxes);
							  }
						  })
					  }}/>
		)
	};

	Cell: React.SFC<CellInfo> = (props: CellInfo, dc: ViewColumn) => {
		const classFromProps = classNames({
			"c-cell-value-wrapper": true,
			"ignored": this.props.isRowIgnored ? this.props.isRowIgnored(props.original) : false
		});
		return (
			<div className={classFromProps}
				 onMouseEnter={(e) => this.onCellMouseOver(e, dc, props.value)}
				 onMouseLeave={() => this.setState({hoveredCell: none})}>
				{this.getCellPresenter(dc.columnDefinition, props.value, props.original)}
			</div>
		)
	};
}
